import { useState, useEffect } from 'react';
import { 
  Globe, Settings, BarChart3, Home, Star, HelpCircle, Mail, Gift,
  Save, Plus, Trash2, GripVertical, Image, ChevronDown, ChevronRight,
  FileText, Eye
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import Sidebar from '@/components/Sidebar';
import ImageUpload from '@/components/ImageUpload';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const iconOptions = [
  'Star', 'FileText', 'CreditCard', 'Users', 'Target', 'ClipboardList',
  'Calendar', 'Music', 'Mic', 'Speaker', 'Radio', 'Headphones', 'Heart',
  'Check', 'Shield', 'Zap', 'Award', 'TrendingUp', 'Clock', 'Mail'
];

export default function FrontendSettingsPage({ user, onLogout, isEmployee, permissions, employeeInfo }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('general');
  
  // Settings state
  const [settings, setSettings] = useState({
    seo: {
      site_title: 'DJ CRM',
      meta_description: 'Professional CRM platform for DJ companies',
      favicon_url: '',
      og_image_url: '',
      google_analytics_id: ''
    },
    home_page: {
      hero: {
        headline: 'Manage Your DJ Business Like a Pro',
        subheadline: 'The all-in-one platform for contracts, invoices, client portals, and more.',
        cta_primary_text: 'Start Free Trial',
        cta_primary_link: '/free-trial',
        cta_secondary_text: 'Login',
        cta_secondary_link: '/auth',
        background_image_url: ''
      },
      show_features_section: true,
      show_screenshots_section: true,
      show_pricing_section: true,
      show_testimonials_section: false,
      custom_css: ''
    },
    features: [],
    faq_items: [],
    contact_page: {
      heading: 'Contact Us',
      subheading: "Have questions? We'd love to hear from you.",
      show_phone: false,
      show_address: false,
      phone: '',
      address: ''
    },
    free_trial: {
      heading: 'Start Your Free Trial',
      subheadline: 'Try all features free for 14 days. No credit card required.',
      trial_days: 14,
      show_features_list: true
    },
    navbar_logo_url: '',
    footer_text: ''
  });

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API}/frontend-settings`);
      if (response.data) {
        setSettings(prev => ({
          ...prev,
          ...response.data,
          seo: { ...prev.seo, ...response.data.seo },
          home_page: { 
            ...prev.home_page, 
            ...response.data.home_page,
            hero: { ...prev.home_page.hero, ...response.data.home_page?.hero }
          },
          contact_page: { ...prev.contact_page, ...response.data.contact_page },
          free_trial: { ...prev.free_trial, ...response.data.free_trial },
          features: response.data.features || [],
          faq_items: response.data.faq_items || []
        }));
      }
    } catch (error) {
      console.error('Failed to fetch frontend settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/frontend-settings`, settings);
      toast.success('Settings saved successfully!');
    } catch (error) {
      console.error('Failed to save settings:', error);
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const addFeature = () => {
    setSettings(prev => ({
      ...prev,
      features: [
        ...prev.features,
        {
          id: `feat_${Date.now()}`,
          title: '',
          description: '',
          icon: 'Star',
          screenshot_url: '',
          order: prev.features.length
        }
      ]
    }));
  };

  const updateFeature = (index, field, value) => {
    setSettings(prev => ({
      ...prev,
      features: prev.features.map((f, i) => 
        i === index ? { ...f, [field]: value } : f
      )
    }));
  };

  const removeFeature = (index) => {
    setSettings(prev => ({
      ...prev,
      features: prev.features.filter((_, i) => i !== index)
    }));
  };

  const addFAQItem = () => {
    setSettings(prev => ({
      ...prev,
      faq_items: [
        ...prev.faq_items,
        {
          id: `faq_${Date.now()}`,
          question: '',
          answer: '',
          order: prev.faq_items.length
        }
      ]
    }));
  };

  const updateFAQItem = (index, field, value) => {
    setSettings(prev => ({
      ...prev,
      faq_items: prev.faq_items.map((item, i) => 
        i === index ? { ...item, [field]: value } : item
      )
    }));
  };

  const removeFAQItem = (index) => {
    setSettings(prev => ({
      ...prev,
      faq_items: prev.faq_items.filter((_, i) => i !== index)
    }));
  };

  if (loading) {
    return (
      <div className="flex min-h-screen bg-obsidian">
        <Sidebar user={user} onLogout={onLogout} isEmployee={isEmployee} permissions={permissions} employeeInfo={employeeInfo} />
        <div className="flex-1 flex items-center justify-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-neon-blue"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen bg-obsidian" data-testid="frontend-settings-page">
      <Sidebar user={user} onLogout={onLogout} isEmployee={isEmployee} permissions={permissions} employeeInfo={employeeInfo} />
      
      <div className="flex-1 overflow-auto">
        <div className="p-8">
          {/* Header */}
          <div className="flex items-center justify-between mb-8">
            <div>
              <h1 className="font-heading text-3xl font-bold text-foreground flex items-center gap-3">
                <Globe className="w-8 h-8 text-neon-blue" />
                Frontend Settings
              </h1>
              <p className="text-muted-foreground mt-1">
                Customize your public-facing website content
              </p>
            </div>
            <div className="flex gap-3">
              <Button
                variant="outline"
                onClick={() => window.open('/', '_blank')}
                className="border-glass-border"
              >
                <Eye className="w-4 h-4 mr-2" />
                Preview Site
              </Button>
              <Button 
                onClick={handleSave} 
                disabled={saving}
                className="bg-neon-blue hover:bg-neon-blue/80"
              >
                <Save className="w-4 h-4 mr-2" />
                {saving ? 'Saving...' : 'Save Changes'}
              </Button>
            </div>
          </div>

          {/* Tabs */}
          <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
            <TabsList className="bg-card/50 p-1">
              <TabsTrigger value="general" className="data-[state=active]:bg-neon-blue/20">
                <Settings className="w-4 h-4 mr-2" />
                General & SEO
              </TabsTrigger>
              <TabsTrigger value="home" className="data-[state=active]:bg-neon-blue/20">
                <Home className="w-4 h-4 mr-2" />
                Home Page
              </TabsTrigger>
              <TabsTrigger value="features" className="data-[state=active]:bg-neon-blue/20">
                <Star className="w-4 h-4 mr-2" />
                Features
              </TabsTrigger>
              <TabsTrigger value="faq" className="data-[state=active]:bg-neon-blue/20">
                <HelpCircle className="w-4 h-4 mr-2" />
                FAQ
              </TabsTrigger>
              <TabsTrigger value="contact" className="data-[state=active]:bg-neon-blue/20">
                <Mail className="w-4 h-4 mr-2" />
                Contact
              </TabsTrigger>
              <TabsTrigger value="trial" className="data-[state=active]:bg-neon-blue/20">
                <Gift className="w-4 h-4 mr-2" />
                Free Trial
              </TabsTrigger>
              <TabsTrigger value="analytics" className="data-[state=active]:bg-neon-blue/20">
                <BarChart3 className="w-4 h-4 mr-2" />
                Analytics
              </TabsTrigger>
            </TabsList>

            {/* General & SEO Tab */}
            <TabsContent value="general" className="space-y-6">
              <div className="glass-card rounded-xl p-6 border border-glass-border">
                <h3 className="font-heading text-lg font-semibold text-foreground mb-4">
                  Site Identity
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label>Site Title</Label>
                    <Input
                      value={settings.seo.site_title}
                      onChange={(e) => setSettings(prev => ({
                        ...prev,
                        seo: { ...prev.seo, site_title: e.target.value }
                      }))}
                      placeholder="DJ CRM"
                      className="bg-muted/20 border-glass-border"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Favicon</Label>
                    <ImageUpload
                      value={settings.seo.favicon_url}
                      onChange={(url) => setSettings(prev => ({
                        ...prev,
                        seo: { ...prev.seo, favicon_url: url }
                      }))}
                      folder="favicon"
                      label="Upload Favicon"
                      previewSize="sm"
                      maxSize={1}
                      enableCrop={false}
                      maxWidth={64}
                      maxHeight={64}
                    />
                  </div>
                  <div className="space-y-2 md:col-span-2">
                    <Label>Meta Description</Label>
                    <Textarea
                      value={settings.seo.meta_description}
                      onChange={(e) => setSettings(prev => ({
                        ...prev,
                        seo: { ...prev.seo, meta_description: e.target.value }
                      }))}
                      placeholder="Professional CRM platform for DJ companies"
                      className="bg-muted/20 border-glass-border"
                      rows={2}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Navbar Logo</Label>
                    <ImageUpload
                      value={settings.navbar_logo_url}
                      onChange={(url) => setSettings(prev => ({
                        ...prev,
                        navbar_logo_url: url
                      }))}
                      folder="logos"
                      label="Upload Logo"
                      previewSize="md"
                      maxWidth={400}
                      maxHeight={100}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Open Graph Image (for social sharing)</Label>
                    <ImageUpload
                      value={settings.seo.og_image_url}
                      onChange={(url) => setSettings(prev => ({
                        ...prev,
                        seo: { ...prev.seo, og_image_url: url }
                      }))}
                      folder="og-images"
                      label="Upload OG Image (1200x630 recommended)"
                      previewSize="md"
                      aspectRatio={1200/630}
                      maxWidth={1200}
                      maxHeight={630}
                    />
                  </div>
                </div>
              </div>

              <div className="glass-card rounded-xl p-6 border border-glass-border">
                <h3 className="font-heading text-lg font-semibold text-foreground mb-4">
                  Analytics
                </h3>
                <div className="space-y-2">
                  <Label>Google Analytics ID</Label>
                  <Input
                    value={settings.seo.google_analytics_id}
                    onChange={(e) => setSettings(prev => ({
                      ...prev,
                      seo: { ...prev.seo, google_analytics_id: e.target.value }
                    }))}
                    placeholder="G-XXXXXXXXXX"
                    className="bg-muted/20 border-glass-border max-w-md"
                  />
                  <p className="text-xs text-muted-foreground">
                    Enter your Google Analytics 4 measurement ID
                  </p>
                </div>
              </div>

              <div className="glass-card rounded-xl p-6 border border-glass-border">
                <h3 className="font-heading text-lg font-semibold text-foreground mb-4">
                  Footer
                </h3>
                <div className="space-y-2">
                  <Label>Footer Text</Label>
                  <Input
                    value={settings.footer_text}
                    onChange={(e) => setSettings(prev => ({
                      ...prev,
                      footer_text: e.target.value
                    }))}
                    placeholder="© 2024 DJ CRM. All rights reserved."
                    className="bg-muted/20 border-glass-border"
                  />
                </div>
              </div>
            </TabsContent>

            {/* Home Page Tab */}
            <TabsContent value="home" className="space-y-6">
              <div className="glass-card rounded-xl p-6 border border-glass-border">
                <h3 className="font-heading text-lg font-semibold text-foreground mb-4">
                  Hero Section
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2 md:col-span-2">
                    <Label>Headline</Label>
                    <Input
                      value={settings.home_page.hero.headline}
                      onChange={(e) => setSettings(prev => ({
                        ...prev,
                        home_page: {
                          ...prev.home_page,
                          hero: { ...prev.home_page.hero, headline: e.target.value }
                        }
                      }))}
                      placeholder="Manage Your DJ Business Like a Pro"
                      className="bg-muted/20 border-glass-border"
                    />
                  </div>
                  <div className="space-y-2 md:col-span-2">
                    <Label>Subheadline</Label>
                    <Textarea
                      value={settings.home_page.hero.subheadline}
                      onChange={(e) => setSettings(prev => ({
                        ...prev,
                        home_page: {
                          ...prev.home_page,
                          hero: { ...prev.home_page.hero, subheadline: e.target.value }
                        }
                      }))}
                      placeholder="The all-in-one platform..."
                      className="bg-muted/20 border-glass-border"
                      rows={2}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Primary CTA Text</Label>
                    <Input
                      value={settings.home_page.hero.cta_primary_text}
                      onChange={(e) => setSettings(prev => ({
                        ...prev,
                        home_page: {
                          ...prev.home_page,
                          hero: { ...prev.home_page.hero, cta_primary_text: e.target.value }
                        }
                      }))}
                      placeholder="Start Free Trial"
                      className="bg-muted/20 border-glass-border"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Primary CTA Link</Label>
                    <Input
                      value={settings.home_page.hero.cta_primary_link}
                      onChange={(e) => setSettings(prev => ({
                        ...prev,
                        home_page: {
                          ...prev.home_page,
                          hero: { ...prev.home_page.hero, cta_primary_link: e.target.value }
                        }
                      }))}
                      placeholder="/free-trial"
                      className="bg-muted/20 border-glass-border"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Secondary CTA Text</Label>
                    <Input
                      value={settings.home_page.hero.cta_secondary_text}
                      onChange={(e) => setSettings(prev => ({
                        ...prev,
                        home_page: {
                          ...prev.home_page,
                          hero: { ...prev.home_page.hero, cta_secondary_text: e.target.value }
                        }
                      }))}
                      placeholder="Login"
                      className="bg-muted/20 border-glass-border"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Secondary CTA Link</Label>
                    <Input
                      value={settings.home_page.hero.cta_secondary_link}
                      onChange={(e) => setSettings(prev => ({
                        ...prev,
                        home_page: {
                          ...prev.home_page,
                          hero: { ...prev.home_page.hero, cta_secondary_link: e.target.value }
                        }
                      }))}
                      placeholder="/auth"
                      className="bg-muted/20 border-glass-border"
                    />
                  </div>
                  <div className="space-y-2 md:col-span-2">
                    <Label>Hero Background Image</Label>
                    <ImageUpload
                      value={settings.home_page.hero.background_image_url}
                      onChange={(url) => setSettings(prev => ({
                        ...prev,
                        home_page: {
                          ...prev.home_page,
                          hero: { ...prev.home_page.hero, background_image_url: url }
                        }
                      }))}
                      folder="hero"
                      label="Upload Hero Background (1920x1080 recommended)"
                      previewSize="lg"
                      maxWidth={1920}
                      maxHeight={1080}
                      quality={80}
                    />
                  </div>
                </div>
              </div>

              <div className="glass-card rounded-xl p-6 border border-glass-border">
                <h3 className="font-heading text-lg font-semibold text-foreground mb-4">
                  Section Visibility
                </h3>
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <Label>Show Features Section</Label>
                      <p className="text-xs text-muted-foreground">Display the features grid on homepage</p>
                    </div>
                    <Switch
                      checked={settings.home_page.show_features_section}
                      onCheckedChange={(checked) => setSettings(prev => ({
                        ...prev,
                        home_page: { ...prev.home_page, show_features_section: checked }
                      }))}
                    />
                  </div>
                  <div className="flex items-center justify-between">
                    <div>
                      <Label>Show Screenshots Section</Label>
                      <p className="text-xs text-muted-foreground">Display app screenshots carousel</p>
                    </div>
                    <Switch
                      checked={settings.home_page.show_screenshots_section}
                      onCheckedChange={(checked) => setSettings(prev => ({
                        ...prev,
                        home_page: { ...prev.home_page, show_screenshots_section: checked }
                      }))}
                    />
                  </div>
                  <div className="flex items-center justify-between">
                    <div>
                      <Label>Show Pricing Section</Label>
                      <p className="text-xs text-muted-foreground">Display pricing plans</p>
                    </div>
                    <Switch
                      checked={settings.home_page.show_pricing_section}
                      onCheckedChange={(checked) => setSettings(prev => ({
                        ...prev,
                        home_page: { ...prev.home_page, show_pricing_section: checked }
                      }))}
                    />
                  </div>
                </div>
              </div>
            </TabsContent>

            {/* Features Tab */}
            <TabsContent value="features" className="space-y-6">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="font-heading text-lg font-semibold text-foreground">
                    Features List
                  </h3>
                  <p className="text-sm text-muted-foreground">
                    Add and manage features displayed on your website
                  </p>
                </div>
                <Button onClick={addFeature} className="bg-neon-blue hover:bg-neon-blue/80">
                  <Plus className="w-4 h-4 mr-2" />
                  Add Feature
                </Button>
              </div>

              <div className="space-y-4">
                {settings.features.length === 0 ? (
                  <div className="glass-card rounded-xl p-12 border border-glass-border text-center">
                    <Star className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                    <p className="text-muted-foreground">No features added yet</p>
                    <Button onClick={addFeature} variant="outline" className="mt-4">
                      <Plus className="w-4 h-4 mr-2" />
                      Add Your First Feature
                    </Button>
                  </div>
                ) : (
                  settings.features.map((feature, index) => (
                    <div key={feature.id} className="glass-card rounded-xl p-6 border border-glass-border">
                      <div className="flex items-start gap-4">
                        <div className="cursor-move text-muted-foreground">
                          <GripVertical className="w-5 h-5" />
                        </div>
                        <div className="flex-1 grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div className="space-y-2">
                            <Label>Title</Label>
                            <Input
                              value={feature.title}
                              onChange={(e) => updateFeature(index, 'title', e.target.value)}
                              placeholder="Feature title"
                              className="bg-muted/20 border-glass-border"
                            />
                          </div>
                          <div className="space-y-2">
                            <Label>Icon</Label>
                            <select
                              value={feature.icon}
                              onChange={(e) => updateFeature(index, 'icon', e.target.value)}
                              className="w-full h-10 px-3 rounded-md bg-muted/20 border border-glass-border text-foreground"
                            >
                              {iconOptions.map(icon => (
                                <option key={icon} value={icon}>{icon}</option>
                              ))}
                            </select>
                          </div>
                          <div className="space-y-2 md:col-span-2">
                            <Label>Description</Label>
                            <Textarea
                              value={feature.description}
                              onChange={(e) => updateFeature(index, 'description', e.target.value)}
                              placeholder="Describe this feature..."
                              className="bg-muted/20 border-glass-border"
                              rows={2}
                            />
                          </div>
                          <div className="space-y-2 md:col-span-2">
                            <Label>Screenshot (optional)</Label>
                            <ImageUpload
                              value={feature.screenshot_url}
                              onChange={(url) => updateFeature(index, 'screenshot_url', url)}
                              folder="features"
                              label="Upload Screenshot (max 1280px wide)"
                              previewSize="lg"
                              maxWidth={1280}
                              maxHeight={800}
                              quality={85}
                            />
                          </div>
                        </div>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => removeFeature(index)}
                          className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </TabsContent>

            {/* FAQ Tab */}
            <TabsContent value="faq" className="space-y-6">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="font-heading text-lg font-semibold text-foreground">
                    FAQ Items
                  </h3>
                  <p className="text-sm text-muted-foreground">
                    Add questions and answers for your FAQ page
                  </p>
                </div>
                <Button onClick={addFAQItem} className="bg-neon-blue hover:bg-neon-blue/80">
                  <Plus className="w-4 h-4 mr-2" />
                  Add Question
                </Button>
              </div>

              <div className="space-y-4">
                {settings.faq_items.length === 0 ? (
                  <div className="glass-card rounded-xl p-12 border border-glass-border text-center">
                    <HelpCircle className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                    <p className="text-muted-foreground">No FAQ items added yet</p>
                    <Button onClick={addFAQItem} variant="outline" className="mt-4">
                      <Plus className="w-4 h-4 mr-2" />
                      Add Your First Question
                    </Button>
                  </div>
                ) : (
                  settings.faq_items.map((item, index) => (
                    <div key={item.id} className="glass-card rounded-xl p-6 border border-glass-border">
                      <div className="flex items-start gap-4">
                        <div className="cursor-move text-muted-foreground">
                          <GripVertical className="w-5 h-5" />
                        </div>
                        <div className="flex-1 space-y-4">
                          <div className="space-y-2">
                            <Label>Question</Label>
                            <Input
                              value={item.question}
                              onChange={(e) => updateFAQItem(index, 'question', e.target.value)}
                              placeholder="What is your question?"
                              className="bg-muted/20 border-glass-border"
                            />
                          </div>
                          <div className="space-y-2">
                            <Label>Answer</Label>
                            <Textarea
                              value={item.answer}
                              onChange={(e) => updateFAQItem(index, 'answer', e.target.value)}
                              placeholder="Provide the answer..."
                              className="bg-muted/20 border-glass-border"
                              rows={3}
                            />
                          </div>
                        </div>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => removeFAQItem(index)}
                          className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </TabsContent>

            {/* Contact Tab */}
            <TabsContent value="contact" className="space-y-6">
              <div className="glass-card rounded-xl p-6 border border-glass-border">
                <h3 className="font-heading text-lg font-semibold text-foreground mb-4">
                  Contact Page Settings
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2 md:col-span-2">
                    <Label>Page Heading</Label>
                    <Input
                      value={settings.contact_page.heading}
                      onChange={(e) => setSettings(prev => ({
                        ...prev,
                        contact_page: { ...prev.contact_page, heading: e.target.value }
                      }))}
                      placeholder="Contact Us"
                      className="bg-muted/20 border-glass-border"
                    />
                  </div>
                  <div className="space-y-2 md:col-span-2">
                    <Label>Subheading</Label>
                    <Input
                      value={settings.contact_page.subheading}
                      onChange={(e) => setSettings(prev => ({
                        ...prev,
                        contact_page: { ...prev.contact_page, subheading: e.target.value }
                      }))}
                      placeholder="Have questions? We'd love to hear from you."
                      className="bg-muted/20 border-glass-border"
                    />
                  </div>
                  <div className="flex items-center justify-between">
                    <div>
                      <Label>Show Phone Number</Label>
                    </div>
                    <Switch
                      checked={settings.contact_page.show_phone}
                      onCheckedChange={(checked) => setSettings(prev => ({
                        ...prev,
                        contact_page: { ...prev.contact_page, show_phone: checked }
                      }))}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Phone Number</Label>
                    <Input
                      value={settings.contact_page.phone}
                      onChange={(e) => setSettings(prev => ({
                        ...prev,
                        contact_page: { ...prev.contact_page, phone: e.target.value }
                      }))}
                      placeholder="+1 (555) 123-4567"
                      className="bg-muted/20 border-glass-border"
                      disabled={!settings.contact_page.show_phone}
                    />
                  </div>
                  <div className="flex items-center justify-between">
                    <div>
                      <Label>Show Address</Label>
                    </div>
                    <Switch
                      checked={settings.contact_page.show_address}
                      onCheckedChange={(checked) => setSettings(prev => ({
                        ...prev,
                        contact_page: { ...prev.contact_page, show_address: checked }
                      }))}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Address</Label>
                    <Input
                      value={settings.contact_page.address}
                      onChange={(e) => setSettings(prev => ({
                        ...prev,
                        contact_page: { ...prev.contact_page, address: e.target.value }
                      }))}
                      placeholder="123 Main St, City, State 12345"
                      className="bg-muted/20 border-glass-border"
                      disabled={!settings.contact_page.show_address}
                    />
                  </div>
                </div>
                <p className="text-sm text-muted-foreground mt-4">
                  Contact form submissions will be sent to your email configured in Settings.
                </p>
              </div>
            </TabsContent>

            {/* Free Trial Tab */}
            <TabsContent value="trial" className="space-y-6">
              <div className="glass-card rounded-xl p-6 border border-glass-border">
                <h3 className="font-heading text-lg font-semibold text-foreground mb-4">
                  Free Trial Page Settings
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2 md:col-span-2">
                    <Label>Page Heading</Label>
                    <Input
                      value={settings.free_trial.heading}
                      onChange={(e) => setSettings(prev => ({
                        ...prev,
                        free_trial: { ...prev.free_trial, heading: e.target.value }
                      }))}
                      placeholder="Start Your Free Trial"
                      className="bg-muted/20 border-glass-border"
                    />
                  </div>
                  <div className="space-y-2 md:col-span-2">
                    <Label>Subheadline</Label>
                    <Input
                      value={settings.free_trial.subheadline}
                      onChange={(e) => setSettings(prev => ({
                        ...prev,
                        free_trial: { ...prev.free_trial, subheadline: e.target.value }
                      }))}
                      placeholder="Try all features free for 14 days."
                      className="bg-muted/20 border-glass-border"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Trial Days</Label>
                    <Input
                      type="number"
                      value={settings.free_trial.trial_days}
                      onChange={(e) => setSettings(prev => ({
                        ...prev,
                        free_trial: { ...prev.free_trial, trial_days: parseInt(e.target.value) || 14 }
                      }))}
                      className="bg-muted/20 border-glass-border"
                    />
                  </div>
                  <div className="flex items-center justify-between">
                    <div>
                      <Label>Show Features List</Label>
                      <p className="text-xs text-muted-foreground">Display features on trial page</p>
                    </div>
                    <Switch
                      checked={settings.free_trial.show_features_list}
                      onCheckedChange={(checked) => setSettings(prev => ({
                        ...prev,
                        free_trial: { ...prev.free_trial, show_features_list: checked }
                      }))}
                    />
                  </div>
                </div>
              </div>
            </TabsContent>

            {/* Analytics Tab - Placeholder for now, will integrate with existing AnalyticsPage */}
            <TabsContent value="analytics" className="space-y-6">
              <div className="glass-card rounded-xl p-6 border border-glass-border">
                <h3 className="font-heading text-lg font-semibold text-foreground mb-4">
                  Website Analytics
                </h3>
                <p className="text-muted-foreground mb-4">
                  View visitor statistics for your public pages.
                </p>
                <Button 
                  variant="outline" 
                  onClick={() => window.location.href = '/analytics'}
                  className="border-glass-border"
                >
                  <BarChart3 className="w-4 h-4 mr-2" />
                  Go to Analytics Dashboard
                </Button>
              </div>
            </TabsContent>
          </Tabs>
        </div>
      </div>
    </div>
  );
}
